import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
  Box,
  InputAdornment,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import MenuItem from "@mui/material/MenuItem";
import { Close } from "@mui/icons-material";
import type {
  IAddPackageModalProps,
  IPackage,
  IService,
} from "../../../../types/package";
import apiService from "../../../../service/Api";
import CustomTextField from "../../../../components/common/Textfield";
import CustomSelect from "../../../../components/common/Select";
import ServiceAutocomplete from "../components/ServiceAutoComplete";
import DescriptionRichTextEditor from "../../components/DescriptionRichTextEditor";
import { tokens } from "../../../../theme";

interface ExtendedAddPackageModalProps extends IAddPackageModalProps {
  isComboPackage?: boolean;
}

const AddPackageModal: React.FC<ExtendedAddPackageModalProps> = ({
  open,
  onClose,
  onPackageAdded,
  selectedPackage,
  isEditMode = false,
  isComboPackage = false, // Added isComboPackage prop with default false
}) => {
  const [packageName, setPackageName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [serviceType, setServiceType] = useState("Funeral");
  const [isActive, setIsActive] = useState(true);
  const [baseServices, setBaseServices] = useState<IService[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  useEffect(() => {
    if (open) {
      if (selectedPackage && isEditMode) {
        setPackageName(selectedPackage.name || "");
        setPrice(selectedPackage.base_price?.toString() || "");
        setDescription(selectedPackage.description || "");
        setServiceType(selectedPackage.package_type || "Funeral");
        setIsActive(selectedPackage.isActive ?? true);
        setBaseServices(
          (selectedPackage.base_services || []).map((service) => ({
            id: service.id.toString(),
            name: service.name,
            price: 0,
            service_category: "Default",
          }))
        );
      } else {
        setPackageName("");
        setPrice("");
        setDescription("");
        setServiceType("Funeral");
        setIsActive(true);
        setBaseServices([]);
      }
      setError(null);
    }
  }, [open, selectedPackage, isEditMode]);

  const validateForm = () => {
    if (!packageName.trim()) {
      setError("Package name is required");
      return false;
    }
    if (!price || Number.parseFloat(price) < 0) {
      setError("Valid price is required");
      return false;
    }
    if (!description.trim()) {
      setError("Description is required");
      return false;
    }
    if (isComboPackage && baseServices.length === 0) {
      setError("At least one base service is required for combo packages");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError(null);
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const serviceIds = isComboPackage
        ? baseServices.map((service) => service.id)
        : [];

      const packageData = {
        name: packageName.trim(),
        base_price: Number.parseFloat(price),
        description: description.trim(),
        package_type: serviceType,
        isActive,
        base_services_ids: serviceIds,
      };

      let response;
      if (selectedPackage?.id && isEditMode) {
        response = await apiService.put<IPackage>(
          `api/packages/package/${selectedPackage.id}/`,
          packageData
        );
        toast.success("Package updated successfully!");
      } else {
        response = await apiService.post<IPackage>(
          "api/packages/package/",
          packageData
        );
        toast.success("Package created successfully!");
      }

      onPackageAdded();
      onClose();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.response?.data?.error ||
        `Failed to ${
          isEditMode ? "update" : "create"
        } package. Please try again.`;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          PaperProps={{
            component: motion.div,
            initial: { opacity: 0, scale: 0.9, y: 20 },
            animate: { opacity: 1, scale: 1, y: 0 },
            exit: { opacity: 0, scale: 0.9, y: 20 },
            transition: { duration: 0.3, ease: "easeOut" },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isEditMode
                ? "Edit Package"
                : isComboPackage
                ? "Add New Combo Package"
                : "Add New Package"}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: colors.primary[300] }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <CustomTextField
                  fullWidth
                  label="Package Name"
                  value={packageName}
                  onChange={(e) => setPackageName(e.target.value)}
                  variant="outlined"
                  required
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <CustomSelect
                  label="Service Type"
                  value={serviceType}
                  onChange={(e: any) =>
                    setServiceType(e.target.value as string)
                  }
                  required
                >
                  <MenuItem value="Funeral">Funeral</MenuItem>
                  <MenuItem value="FuneralHome">Funeral Home</MenuItem>
                  <MenuItem value="Memorial">Memorial</MenuItem>
                  <MenuItem value="Church">Church</MenuItem>
                  <MenuItem value="Conference">Conference</MenuItem>
                  <MenuItem value="Event">Event</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </CustomSelect>
              </motion.div>

              {isComboPackage && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <ServiceAutocomplete
                    value={baseServices}
                    onChange={setBaseServices}
                  />
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Typography variant="subtitle1">Package Description</Typography>
                <DescriptionRichTextEditor
                  value={description}
                  onChange={(html) => setDescription(html)}
                  placeholder="Enter package description..."
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <CustomTextField
                  fullWidth
                  label="Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  variant="outlined"
                  type="number"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#8b5a3c",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#8b5a3c",
                          },
                      }}
                    />
                  }
                  label="Active Package"
                />
              </motion.div>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
            <Button
              onClick={onClose}
              sx={{ color: "#666" }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                backgroundColor: colors.primary[400],
                "&:hover": { backgroundColor: colors.primary[300] },
              }}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                ? "Update Package"
                : "Add Package"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default AddPackageModal;
